/**
 * Helpers for generic task links: entity_type + entity_id → API type, display label, Hub URLs.
 */

export const ENTITY_TYPE_DISPLAY_LABELS: Record<string, string> = {
  contacts: "Contacts",
  companies: "Companies",
  deals: "Deals",
  tickets: "Tickets",
  tasks: "Tasks",
  time_entries: "Time entries",
  employees: "Employees",
  users: "Users",
  contracts: "Contracts",
  communications: "Communications",
  recurring_transactions: "Recurring transactions",
  recurring_charges: "Recurring charges",
  offerings: "Offerings",
};

/** Entity keys accepted by POST /api/entities/[entity]/by-ids */
const API_ENTITY_TYPES = new Set([
  "contacts",
  "companies",
  "deals",
  "tickets",
  "tasks",
  "communications",
  "offerings",
]);

const SINGULAR_TO_API_PLURAL: Record<string, string> = {
  contact: "contacts",
  company: "companies",
  deal: "deals",
  ticket: "tickets",
  task: "tasks",
  communication: "communications",
  offering: "offerings",
};

export function normalizeEntityTypeForApi(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const t = String(raw).trim().toLowerCase();
  if (!t) return null;
  if (API_ENTITY_TYPES.has(t)) return t;
  return SINGULAR_TO_API_PLURAL[t] ?? null;
}

export function formatEntityTypeForDisplay(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || String(raw).trim() === "") return "—";
  const key = String(raw).trim().toLowerCase();
  return ENTITY_TYPE_DISPLAY_LABELS[key] ?? String(raw);
}

export function getLinkedEntityTitle(row: Record<string, unknown>, apiEntityType: string): string {
  const title = row.title;
  const name = row.name;
  const subject = row.subject;
  if (typeof title === "string" && title.trim().length > 0) return title.trim();
  if (typeof name === "string" && name.trim().length > 0) return name.trim();
  if (apiEntityType === "tickets") {
    const num = row.ticket_number;
    if (typeof num === "number" && Number.isFinite(num)) {
      return `WTC-${String(num).padStart(6, "0")}`;
    }
  }
  if (typeof subject === "string" && subject.trim().length > 0) return subject.trim();
  const id = row.id;
  if (typeof id === "string" && id.length >= 8) return `${id.slice(0, 8)}…`;
  return "Linked record";
}

/**
 * Full-page or list+modal URL for the linked CRM record.
 */
export function getLinkedEntityHref(apiEntityType: string, id: string): string | null {
  switch (apiEntityType) {
    case "contacts":
      return `/contacts/${id}`;
    case "companies":
      return `/companies/${id}`;
    case "deals":
      return `/deals/${id}`;
    case "tickets":
      return `/tickets/${id}`;
    case "tasks":
      return `/tasks/${id}`;
    case "communications":
      return `/communications/${id}`;
    case "offerings":
      return `/offerings?offeringsId=${encodeURIComponent(id)}&modal=offerings-detail`;
    default:
      return null;
  }
}
